import { Select, MenuItem, Divider, SelectChangeEvent, InputLabel, Chip, Stack, Typography } from '@unity/cloud-ui';
import { useTranslations } from 'next-intl';
import { usePathname, useRouter } from '@/i18n/routing';
import type { ManifestSchema } from '@/lib/content-loader/schemas/manifest.schema';

export type AvailableVersions = NonNullable<ManifestSchema>['versions'];

export const VersionPicker = (props: { versions: AvailableVersions }) => {
  const t = useTranslations();
  const pathname = usePathname();
  const router = useRouter();

  if (props.versions.length === 0) {
    return null;
  }

  const selectedVersion =
    props.versions.find((version) => pathname.toLowerCase().includes(version.versionNumber.toLowerCase())) ??
    props.versions[0];

  const handleVersionChange = (event: SelectChangeEvent<string>) => {
    const newVersion = props.versions.find((version) => version.title === event.target.value);
    if (newVersion) {
      const cleanedPathname = pathname.replace(
        new RegExp(`${selectedVersion.versionNumber}`),
        `${newVersion.versionNumber}`,
      );
      router.replace(cleanedPathname);
    }
  };

  const getChipText = (version: AvailableVersions[number]) => {
    if (version.supportedFrom !== undefined) {
      return version.supportedFrom;
    }
    return version.status === 'supported' ? t('version-picker:supported') : t('version-picker:legacy');
  };

  return (
    <Stack direction="row">
      <InputLabel id="version-picker-label" sx={{ color: 'text.secondary', mr: 0.5 }}>
        {t('sidebar:section:version') + ':'}
      </InputLabel>
      <Select
        value={selectedVersion.title}
        size="small"
        aria-label={t('feedback:select:aria-label')}
        labelId="version-picker-label"
        MenuProps={{
          disableScrollLock: true,
          sx: {
            '&& .Mui-selected': {
              bgcolor: (theme) => `${theme.vars.palette.primary.light} !important`,
              '&:hover': { bgcolor: 'primary.light' },
            },
            '&& .MuiMenu-list': {
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
            },
          },
        }}
        sx={({ vars }) => ({
          borderRadius: '6px',
          '& > fieldset': { borderWidth: '0' },
          '&& .MuiSelect-select': {
            width: '250px',
            boxSizing: 'border-box',
            display: 'flex',
            justifyContent: 'space-between',
            height: 36,
            borderRadius: '6px',
            backgroundColor: vars.palette.background.paperElevation0,
            '& > div': {
              margin: 0,
              alignSelf: 'center',
              height: '1.35rem',
            },
            '&:hover': { bgcolor: 'action.hover' },
          },
        })}
        onChange={handleVersionChange}
      >
        {props.versions.map((version) => {
          return (
            <MenuItem
              key={version.versionNumber}
              value={version.title}
              sx={{ display: 'flex', justifyContent: 'space-between' }}
            >
              <Typography sx={{ width: '60%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {version.title}
              </Typography>
              <Chip
                label={getChipText(version)}
                squared
                color={version.status === 'supported' ? 'success' : 'default'}
                variant={'outlined'}
                sx={{ float: 'right', opacity: version.status === 'supported' ? 1 : 0.7 }}
              />
            </MenuItem>
          );
        })}
      </Select>
      <Divider />
    </Stack>
  );
};
